// PDF.js viewer implementation
// PDF.js is loaded from CDN in the layout

class PDFViewer {
  constructor(container, url) {
    this.container = container;
    this.url = url;
    this.pdfDoc = null;
    this.scale = 1.0; // Start with scale 1.0 for better fit
    this.canvases = [];
    this.renderingPages = new Set();
  }

  async initialize() {
    // Create controls container
    const controls = document.createElement('div');
    controls.className = 'pdf-controls';
    controls.style.cssText = 'text-align: center; padding: 10px; background: #f5f5f5; border-bottom: 1px solid #ddd;';

    // Page info
    const pageInfo = document.createElement('span');
    pageInfo.id = 'page-info';
    pageInfo.style.margin = '0 15px';
    pageInfo.textContent = 'Loading PDF...';

    // Zoom controls
    const zoomOut = document.createElement('button');
    zoomOut.textContent = 'Zoom Out';
    zoomOut.className = 'btn btn-sm btn-secondary';
    zoomOut.style.marginRight = '10px';
    zoomOut.onclick = () => this.zoomOut();

    const zoomReset = document.createElement('button');
    zoomReset.textContent = 'Fit Width';
    zoomReset.className = 'btn btn-sm btn-secondary';
    zoomReset.style.marginRight = '10px';
    zoomReset.onclick = () => this.resetZoom();

    const zoomIn = document.createElement('button');
    zoomIn.textContent = 'Zoom In';
    zoomIn.className = 'btn btn-sm btn-secondary';
    zoomIn.onclick = () => this.zoomIn();

    controls.appendChild(pageInfo);
    controls.appendChild(zoomOut);
    controls.appendChild(zoomReset);
    controls.appendChild(zoomIn);

    // Canvas container with scroll - this will hold all pages
    const canvasContainer = document.createElement('div');
    canvasContainer.className = 'pdf-canvas-container';
    canvasContainer.style.cssText = 'overflow: auto; height: calc(70vh - 50px); background: #525659; text-align: center;';

    // Add to container
    this.container.innerHTML = '';
    this.container.appendChild(controls);
    this.container.appendChild(canvasContainer);

    // Load PDF
    try {
      if (typeof window.pdfjsLib === 'undefined') {
        throw new Error('PDF.js library not loaded. Please check your internet connection.');
      }
      const loadingTask = window.pdfjsLib.getDocument(this.url);
      this.pdfDoc = await loadingTask.promise;

      document.getElementById('page-info').textContent = `${this.pdfDoc.numPages} page${this.pdfDoc.numPages > 1 ? 's' : ''}`;

      // Render all pages
      await this.renderAllPages(canvasContainer);
    } catch (error) {
      console.error('Error loading PDF:', error);
      this.container.innerHTML = `
        <div style="padding: 40px; text-align: center; color: #dc3545;">
          <h4>Error loading PDF</h4>
          <p>Unable to load the PDF file. Please try downloading it instead.</p>
          <p style="font-size: 12px; color: #666;">Error: ${error.message}</p>
        </div>
      `;
    }
  }

  async renderAllPages(container) {
    const numPages = this.pdfDoc.numPages;

    // Get the first page to calculate the optimal scale for fit-to-width
    const firstPage = await this.pdfDoc.getPage(1);
    const viewport = firstPage.getViewport({ scale: 1.0 });

    // Calculate scale to fit container width (with some padding)
    const containerWidth = container.clientWidth - 40; // 20px padding on each side
    this.scale = containerWidth / viewport.width;

    // Create and render all pages
    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      await this.renderPage(pageNum, container);
    }
  }

  async renderPage(pageNum, container) {
    if (this.renderingPages.has(pageNum)) {
      return; // Already rendering this page
    }

    this.renderingPages.add(pageNum);

    try {
      const page = await this.pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale: this.scale });

      // Create canvas for this page
      const canvas = document.createElement('canvas');
      canvas.className = 'pdf-page-canvas';
      canvas.style.cssText = 'display: block; margin: 10px auto; box-shadow: 0 2px 8px rgba(0,0,0,0.3);';
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      canvas.dataset.pageNum = pageNum;

      const ctx = canvas.getContext('2d');

      const renderContext = {
        canvasContext: ctx,
        viewport: viewport
      };

      await page.render(renderContext).promise;

      // Add page number label
      const pageLabel = document.createElement('div');
      pageLabel.style.cssText = 'color: #fff; font-size: 12px; margin: 5px 0; text-align: center;';
      pageLabel.textContent = `Page ${pageNum}`;

      container.appendChild(canvas);
      container.appendChild(pageLabel);

      this.canvases.push({ canvas, pageNum, ctx });
    } catch (error) {
      console.error(`Error rendering page ${pageNum}:`, error);
    } finally {
      this.renderingPages.delete(pageNum);
    }
  }

  async reRenderAllPages() {
    const container = this.container.querySelector('.pdf-canvas-container');

    // Clear existing canvases
    container.innerHTML = '';
    this.canvases = [];
    this.renderingPages.clear();

    // Re-render all pages with new scale
    const numPages = this.pdfDoc.numPages;
    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      await this.renderPage(pageNum, container);
    }
  }

  zoomIn() {
    this.scale += 0.25;
    this.reRenderAllPages();
  }

  zoomOut() {
    if (this.scale <= 0.5) return;
    this.scale -= 0.25;
    this.reRenderAllPages();
  }

  async resetZoom() {
    const container = this.container.querySelector('.pdf-canvas-container');
    const containerWidth = container.clientWidth - 40;

    const firstPage = await this.pdfDoc.getPage(1);
    const viewport = firstPage.getViewport({ scale: 1.0 });

    this.scale = containerWidth / viewport.width;
    this.reRenderAllPages();
  }

  destroy() {
    if (this.pdfDoc) {
      this.pdfDoc.destroy();
    }
    this.canvases = [];
    this.renderingPages.clear();
  }
}

// Export for global use
window.PDFViewer = PDFViewer;

// Initialize PDF viewers when modals open
document.addEventListener('DOMContentLoaded', function() {
  let currentPDFViewer = null;

  // When modal opens, initialize PDF viewer
  $(document).on('shown.bs.modal', '.window-popup-wrapper', function() {
    const $pdfContainer = $(this).find('.pdf-viewer-container');
    if ($pdfContainer.length > 0) {
      const pdfUrl = $pdfContainer.data('pdf-url');
      if (pdfUrl && !$pdfContainer.data('initialized')) {
        currentPDFViewer = new PDFViewer($pdfContainer[0], pdfUrl);
        currentPDFViewer.initialize();
        $pdfContainer.data('initialized', true);
      }
    }
  });

  // Clean up when modal closes
  $(document).on('hidden.bs.modal', '.window-popup-wrapper', function() {
    const $pdfContainer = $(this).find('.pdf-viewer-container');
    if ($pdfContainer.length > 0 && $pdfContainer.data('initialized')) {
      if (currentPDFViewer) {
        currentPDFViewer.destroy();
        currentPDFViewer = null;
      }
      $pdfContainer.empty();
      $pdfContainer.data('initialized', false);
    }
  });
});
